import React, { useState, useEffect } from 'react';
import { Link, Plus, RefreshCw, Copy, ExternalLink, Layers, AlertCircle } from 'lucide-react';
import { COLORS } from '../../../../utils/constants';
import { adminAPI } from '../../../../services/api';
import { useAdminToast } from '../../../../hooks/useAdminToast';

const WalletsTab = ({ clientId, details, onAddWallet }) => {
  const { toast } = useAdminToast();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [multiChainBalances, setMultiChainBalances] = useState({});
  const [loadingMultiChain, setLoadingMultiChain] = useState({});

  useEffect(() => {
    loadWallets();
  }, [clientId]);

  const loadWallets = async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getClientWallets(clientId);
      const walletsData = res.data.wallets || [];
      setWallets(walletsData);
      
      // Auto-fetch multi-chain balances for each wallet
      walletsData.forEach(wallet => {
        if (wallet.address) {
          fetchMultiChainBalance(wallet.address);
        }
      });
    } catch (err) {
      console.error('Failed to load wallets:', err);
      setWallets(details?.wallets || []);
    } finally {
      setLoading(false);
    }
  };

  const fetchMultiChainBalance = async (address) => {
    setLoadingMultiChain(prev => ({ ...prev, [address]: true }));
    try {
      const res = await adminAPI.getWalletMultiChainBalance(address);
      setMultiChainBalances(prev => ({ ...prev, [address]: res.data.balances }));
    } catch (err) {
      console.error('Failed to fetch multi-chain balance:', err);
    } finally {
      setLoadingMultiChain(prev => ({ ...prev, [address]: false }));
    }
  };

  const refreshBalances = async () => {
    setRefreshing(true);
    await loadWallets();
    setRefreshing(false);
  };

  const copyAddress = (address) => {
    navigator.clipboard.writeText(address);
    toast.info('Адрес скопирован');
  };

  const chainColors = {
    ETH: '#627eea',
    BSC: '#f3ba2f',
    POLYGON: '#8247e5',
    MATIC: '#8247e5',
    ARBITRUM: '#28a0f0',
    OPTIMISM: '#ff0420',
    AVALANCHE: '#e84142',
    AVAX: '#e84142',
    BASE: '#0052ff',
  };

  const chainExplorerUrls = {
    ETH: 'https://etherscan.io/address/',
    BSC: 'https://bscscan.com/address/',
    POLYGON: 'https://polygonscan.com/address/',
    ARBITRUM: 'https://arbiscan.io/address/',
  };

  const holdings = details?.holdings || [];
  const spotTotal = details?.balance?.spot_balance || holdings.reduce((sum, h) => {
    const amount = h.quantity || h.amount || 0;
    const price = h.avg_buy_price || 0;
    return sum + (amount * price);
  }, 0);

  const futuresBalance = details?.balance?.exchange_balance || 0;
  const totalPnl = details?.balance?.total_pnl || 0;

  return (
    <div>
      {/* Portfolio Summary */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '12px',
        marginBottom: '20px',
      }}>
        <div style={{
          padding: '16px',
          backgroundColor: COLORS.background,
          borderRadius: '10px',
          textAlign: 'center',
        }}>
          <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>Futures баланс</div>
          <div style={{ color: COLORS.primary, fontSize: '20px', fontWeight: '700' }}>${futuresBalance.toFixed(2)}</div>
        </div>
        <div style={{
          padding: '16px',
          backgroundColor: COLORS.background,
          borderRadius: '10px',
          textAlign: 'center',
        }}>
          <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>Spot портфель</div>
          <div style={{ color: COLORS.success, fontSize: '20px', fontWeight: '700' }}>${spotTotal.toFixed(2)}</div>
        </div>
        <div style={{
          padding: '16px',
          backgroundColor: COLORS.background,
          borderRadius: '10px',
          textAlign: 'center',
        }}>
          <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>PnL</div>
          <div style={{ color: totalPnl >= 0 ? COLORS.success : COLORS.danger, fontSize: '20px', fontWeight: '700' }}>
            {totalPnl >= 0 ? '+' : ''}${totalPnl.toFixed(2)}
          </div>
        </div>
        <div style={{
          padding: '16px',
          backgroundColor: COLORS.background,
          borderRadius: '10px',
          textAlign: 'center',
        }}>
          <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>Всего</div>
          <div style={{ color: COLORS.text, fontSize: '20px', fontWeight: '700' }}>${(futuresBalance + spotTotal).toFixed(2)}</div>
        </div>
      </div>

      {/* Wallets Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h4 style={{ color: COLORS.text, fontSize: '15px', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Layers size={18} color={COLORS.primary} />
          Подключенные кошельки
        </h4>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={refreshBalances}
            disabled={refreshing}
            style={{
              padding: '6px 12px',
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '6px',
              color: COLORS.textSecondary,
              cursor: refreshing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '12px',
            }}
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Загрузка...' : 'Обновить'}
          </button>
          <button
            onClick={onAddWallet}
            style={{
              padding: '6px 12px',
              backgroundColor: COLORS.primary,
              border: 'none',
              borderRadius: '6px',
              color: '#000',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '12px',
              fontWeight: '600',
            }}
          >
            <Plus size={14} />
            Добавить
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: COLORS.textSecondary }}>
          Загрузка кошельков...
        </div>
      ) : wallets.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: COLORS.background,
          borderRadius: '10px',
          color: COLORS.textSecondary,
        }}>
          <Link size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <div>Нет подключенных кошельков</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {wallets.map((wallet, idx) => {
            const chainBalances = multiChainBalances[wallet.address];
            const isLoadingChain = loadingMultiChain[wallet.address];
            
            return (
              <div
                key={wallet.id || idx}
                style={{
                  padding: '16px',
                  backgroundColor: COLORS.background,
                  borderRadius: '10px',
                  border: `1px solid ${COLORS.border}`,
                }}
              >
                {/* Wallet Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      backgroundColor: `${chainColors[wallet.chain] || COLORS.primary}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: chainColors[wallet.chain] || COLORS.primary,
                      fontWeight: '700',
                      fontSize: '11px',
                    }}>
                      {wallet.chain || 'ETH'}
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: COLORS.text, fontSize: '14px', fontFamily: 'monospace' }}>
                          {wallet.address?.slice(0, 8)}...{wallet.address?.slice(-6)}
                        </span>
                        {wallet.is_primary && (
                          <span style={{
                            padding: '2px 6px',
                            backgroundColor: `${COLORS.primary}20`,
                            color: COLORS.primary,
                            fontSize: '10px',
                            borderRadius: '4px',
                            fontWeight: '600',
                          }}>
                            Основной
                          </span>
                        )}
                      </div>
                      <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginTop: '2px' }}>
                        Подключен: {wallet.connected_at ? new Date(wallet.connected_at).toLocaleDateString('ru-RU') : '-'}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={() => copyAddress(wallet.address)}
                      style={{
                        padding: '6px',
                        backgroundColor: COLORS.card,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '6px',
                        color: COLORS.textSecondary,
                        cursor: 'pointer',
                      }}
                      title="Копировать адрес"
                    >
                      <Copy size={14} />
                    </button>
                    <button
                      onClick={() => fetchMultiChainBalance(wallet.address)}
                      disabled={isLoadingChain}
                      style={{
                        padding: '6px',
                        backgroundColor: COLORS.card,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '6px',
                        color: COLORS.primary,
                        cursor: isLoadingChain ? 'not-allowed' : 'pointer',
                      }}
                      title="Обновить балансы"
                    >
                      <RefreshCw size={14} className={isLoadingChain ? 'animate-spin' : ''} />
                    </button>
                  </div>
                </div>

                {/* Multi-Chain Balances */}
                {chainBalances ? (
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '8px', textTransform: 'uppercase' }}>
                      Балансы в разных сетях
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                      {Object.entries(chainBalances).map(([chainName, data]) => (
                        <div
                          key={chainName}
                          style={{
                            padding: '10px 12px',
                            backgroundColor: COLORS.card,
                            borderRadius: '8px',
                            border: `1px solid ${chainColors[chainName] || COLORS.border}20`,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              backgroundColor: `${chainColors[chainName] || COLORS.primary}20`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: chainColors[chainName] || COLORS.primary,
                              fontSize: '9px',
                              fontWeight: '700',
                            }}>
                              {chainName.slice(0, 3)}
                            </div>
                            <div>
                              <div style={{ color: COLORS.text, fontSize: '12px', fontWeight: '600' }}>
                                {chainName}
                              </div>
                              <div style={{ color: COLORS.textSecondary, fontSize: '10px' }}>
                                {data.symbol}
                              </div>
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            {data.error ? (
                              <div style={{ color: COLORS.danger, fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <AlertCircle size={12} /> Ошибка
                              </div>
                            ) : (
                              <>
                                <div style={{ color: data.balance > 0 ? COLORS.success : COLORS.text, fontSize: '13px', fontWeight: '600' }}>
                                  {data.balance?.toFixed(6) || '0'}
                                </div>
                                <a
                                  href={`${chainExplorerUrls[chainName] || 'https://etherscan.io/address/'}${wallet.address}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: COLORS.primary, fontSize: '10px', textDecoration: 'none' }}
                                >
                                  Explorer →
                                </a>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : isLoadingChain ? (
                  <div style={{ padding: '12px', textAlign: 'center', color: COLORS.textSecondary, fontSize: '12px' }}>
                    <RefreshCw size={16} className="animate-spin" style={{ marginRight: '8px' }} />
                    Загрузка балансов...
                  </div>
                ) : (
                  <button
                    onClick={() => fetchMultiChainBalance(wallet.address)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: `${COLORS.primary}10`,
                      border: `1px dashed ${COLORS.primary}`,
                      borderRadius: '8px',
                      color: COLORS.primary,
                      fontSize: '12px',
                      cursor: 'pointer',
                      marginBottom: '10px',
                    }}
                  >
                    Загрузить балансы в сетях
                  </button>
                )}

                {/* Primary Balance (Original) */}
                <div style={{
                  padding: '10px 12px',
                  backgroundColor: COLORS.card,
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                  <span style={{ color: COLORS.textSecondary, fontSize: '13px' }}>
                    Основной баланс ({wallet.native_symbol || wallet.chain || 'ETH'})
                  </span>
                  <span style={{ color: COLORS.text, fontSize: '15px', fontWeight: '600' }}>
                    {wallet.balance !== undefined ? wallet.balance.toFixed(6) : '—'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Spot Holdings */}
      {holdings.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <h4 style={{ color: COLORS.text, fontSize: '15px', fontWeight: '600', marginBottom: '12px' }}>
            Spot портфель
          </h4>
          <div style={{
            backgroundColor: COLORS.background,
            borderRadius: '10px',
            border: `1px solid ${COLORS.border}`,
            overflow: 'hidden',
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr',
              padding: '10px 16px',
              backgroundColor: COLORS.card,
              borderBottom: `1px solid ${COLORS.border}`,
            }}>
              <span style={{ color: COLORS.textSecondary, fontSize: '11px', fontWeight: '600' }}>АКТИВ</span>
              <span style={{ color: COLORS.textSecondary, fontSize: '11px', fontWeight: '600', textAlign: 'right' }}>КОЛИЧЕСТВО</span>
              <span style={{ color: COLORS.textSecondary, fontSize: '11px', fontWeight: '600', textAlign: 'right' }}>СР. ЦЕНА</span>
              <span style={{ color: COLORS.textSecondary, fontSize: '11px', fontWeight: '600', textAlign: 'right' }}>СТОИМОСТЬ</span>
            </div>
            
            {holdings.map((h, idx) => {
              const amount = h.quantity || h.amount || 0;
              const price = h.avg_buy_price || 0;
              const value = amount * price;
              
              return (
                <div
                  key={h.id || idx}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 1fr',
                    padding: '12px 16px',
                    borderBottom: idx < holdings.length - 1 ? `1px solid ${COLORS.border}20` : 'none',
                  }}
                >
                  <span style={{ color: COLORS.text, fontSize: '13px', fontWeight: '600' }}>{h.symbol || h.asset}</span>
                  <span style={{ color: COLORS.text, fontSize: '13px', textAlign: 'right' }}>{amount.toFixed(6)}</span>
                  <span style={{ color: COLORS.textSecondary, fontSize: '13px', textAlign: 'right' }}>${price.toFixed(2)}</span>
                  <span style={{ color: COLORS.success, fontSize: '13px', fontWeight: '600', textAlign: 'right' }}>${value.toFixed(2)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletsTab;
